import { useEffect, useState } from "react";
import {
  Inbox,
  CalendarDays,
  Radio,
  SquareKanban,
  ChartColumn,
  Settings,
  Activity,
  LogOut,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useConnection, useLive, type Metrics, type Channel } from "./lib/api";
import { Fila } from "./screens/Fila";
import { Funil } from "./screens/Funil";
import { Lead } from "./screens/Lead";
import { Config } from "./screens/Config";
import { Analise } from "./screens/Analise";
import { AoVivo } from "./screens/AoVivo";
import { Agenda } from "./screens/Agenda";
import { Login } from "./screens/Login";
import { Saude } from "./screens/Saude";

type Tela = "fila" | "agenda" | "aovivo" | "funil" | "analise" | "saude" | "config";

/**
 * As secoes, com o que cada uma responde.
 *
 * O subtitulo nao e enfeite: e a pergunta que a tela existe para responder.
 * Quem abre o painel pela primeira vez entende o que cada area faz sem
 * precisar clicar em todas.
 */
const SECOES: { id: Tela; rotulo: string; icone: LucideIcon; pergunta: string }[] = [
  { id: "fila", rotulo: "Fila", icone: Inbox, pergunta: "Quem precisa de você agora" },
  { id: "agenda", rotulo: "Agenda", icone: CalendarDays, pergunta: "As conversas que a IA marcou" },
  { id: "aovivo", rotulo: "Ao vivo", icone: Radio, pergunta: "O que está acontecendo nas conversas neste momento" },
  { id: "funil", rotulo: "Funil", icone: SquareKanban, pergunta: "Cada lead na etapa em que está" },
  { id: "analise", rotulo: "Análise", icone: ChartColumn, pergunta: "O que o atendimento entregou, e quanto custou" },
  { id: "saude", rotulo: "Saúde", icone: Activity, pergunta: "O que está de pé, e o que não está" },
  { id: "config", rotulo: "Configurações", icone: Settings, pergunta: "Como a IA se apresenta, conversa e avisa" },
];

export function App() {
  /*
   * O portao.
   *
   * `null` enquanto nao se sabe -- e nao `false`. Comecar deslogado faria a
   * tela de login piscar a cada F5 para quem ja esta autenticado, e piscar
   * assim parece bug antes de parecer seguranca.
   */
  const [autenticado, setAutenticado] = useState<boolean | null>(null);

  useEffect(() => {
    void fetch("/api/auth/estado")
      .then((r) => r.json())
      .then((d: { autenticado?: boolean }) => setAutenticado(Boolean(d.autenticado)))
      .catch(() => setAutenticado(false));
  }, []);

  if (autenticado === null) return null;
  if (!autenticado) return <Login onEntrou={() => setAutenticado(true)} />;

  return <Painel onSair={() => setAutenticado(false)} />;
}

function Painel({ onSair }: { onSair: () => void }) {
  const [tela, setTela] = useState<Tela>("fila");
  const [leadId, setLeadId] = useState<string | null>(null);
  const online = useConnection();
  const { data: m } = useLive<Metrics>("/metrics", ["handoffs", "leads"]);
  const { data: canais } = useLive<Channel[]>("/channels", ["channels"]);
  // Quantos problemas sérios agora -- o menu mostra o número sem ninguém
  // precisar abrir a tela.
  const [problemas, setProblemas] = useState(0);
  useEffect(() => {
    const ler = () =>
      fetch("/api/saude")
        .then((r) => r.json())
        .then((d: { problemas?: { nivel: string }[] }) =>
          setProblemas((d.problemas ?? []).filter((p) => p.nivel === "erro").length),
        )
        .catch(() => undefined);
    ler();
    const t = setInterval(ler, 60_000);
    return () => clearInterval(t);
  }, []);

  // Canal caido e a falha mais silenciosa do sistema: nada quebra, nada
  // aparece no log do painel, e as mensagens simplesmente param de chegar.
  // Por isso vira aviso permanente, em toda tela.
  const canaisComProblema =
    canais?.filter((c) => c.active && c.status !== "connected") ?? [];

  const contador = (t: Tela): { n: number; tom: "warm" | "danger" } | null => {
    if (t === "fila" && (m?.esperando_humano ?? 0) > 0)
      return { n: m!.esperando_humano, tom: "warm" };
    if (t === "config" && canaisComProblema.length > 0)
      return { n: canaisComProblema.length, tom: "danger" };
    if (t === "saude" && problemas > 0) return { n: problemas, tom: "danger" };
    return null;
  };

  const ir = (t: Tela) => {
    setTela(t);
    setLeadId(null);
  };

  const atual = SECOES.find((s) => s.id === tela)!;

  const sair = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    onSair();
  };

  return (
    <div className="min-h-full lg:pl-60">
      {/* ------------------------------------------------------------ lateral */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-rule bg-surface/60 backdrop-blur-xl">
        <div className="h-16 flex items-center gap-2.5 px-5">
          <Marca />
        </div>

        <nav className="flex-1 px-3 py-2 space-y-0.5">
          {SECOES.map((s) => {
            const ativo = tela === s.id && !leadId;
            const c = contador(s.id);
            return (
              <button
                key={s.id}
                onClick={() => ir(s.id)}
                className={`group w-full flex items-center gap-3 px-3 h-9 rounded-lg text-sm transition-colors ${
                  ativo
                    ? "bg-accent-dim text-ink font-medium"
                    : "text-ink-2 hover:text-ink hover:bg-white/[0.04]"
                }`}
              >
                <s.icone
                  size={17}
                  strokeWidth={ativo ? 2.3 : 1.9}
                  className={ativo ? "text-accent" : "text-ink-3 group-hover:text-ink-2"}
                />
                <span className="flex-1 text-left">{s.rotulo}</span>
                {c && <Contador n={c.n} tom={c.tom} />}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-rule space-y-1">
          <Conexao online={online} />
          <button
            onClick={sair}
            className="w-full flex items-center gap-3 px-3 h-9 rounded-lg text-sm text-ink-3 hover:text-ink hover:bg-white/[0.04] transition-colors"
          >
            <LogOut size={16} />
            Sair
          </button>
        </div>
      </aside>

      {/* ------------------------------------------------- topo, telas menores */}
      <header className="lg:hidden sticky top-0 z-20 border-b border-rule bg-ground/80 backdrop-blur-xl">
        <div className="h-14 px-4 flex items-center gap-3">
          <Marca />
          <div className="ml-auto flex items-center gap-1">
            <Conexao online={online} compacto />
            <button
              onClick={sair}
              className="w-9 h-9 grid place-items-center rounded-lg text-ink-3 hover:text-ink hover:bg-white/[0.04]"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <nav className="sem-barra flex gap-1 px-3 pb-2 overflow-x-auto">
          {SECOES.map((s) => {
            const ativo = tela === s.id && !leadId;
            const c = contador(s.id);
            return (
              <button
                key={s.id}
                onClick={() => ir(s.id)}
                className={`shrink-0 flex items-center gap-2 px-3 h-8 rounded-lg text-sm ${
                  ativo ? "bg-accent-dim text-ink font-medium" : "text-ink-3"
                }`}
              >
                <s.icone size={15} className={ativo ? "text-accent" : ""} />
                {s.rotulo}
                {c && <Contador n={c.n} tom={c.tom} />}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {canaisComProblema.length > 0 && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-danger/30 bg-danger-dim px-4 py-3">
            <span className="w-8 h-8 rounded-lg bg-danger/15 text-danger grid place-items-center shrink-0">
              <TriangleAlert size={16} />
            </span>
            <div className="text-sm min-w-0 flex-1">
              <div className="text-ink font-medium">
                {canaisComProblema.map((c) => c.label).join(", ")}{" "}
                {canaisComProblema.length === 1 ? "está" : "estão"} fora do ar
              </div>
              <div className="text-ink-2 text-xs mt-0.5">
                Nenhuma mensagem desses números chega nem é respondida.
              </div>
            </div>
            <button
              onClick={() => ir("config")}
              className="shrink-0 px-3 h-8 rounded-lg text-sm font-medium bg-danger/15 text-danger hover:bg-danger/25 transition-colors"
            >
              Resolver
            </button>
          </div>
        )}

        {!leadId && (
          <div className="mb-6">
            <h1 className="text-xl font-semibold tracking-tight">{atual.rotulo}</h1>
            <p className="text-sm text-ink-3 mt-1">{atual.pergunta}</p>
          </div>
        )}

        {/* A chave troca a cada navegacao: a tela nova entra com a animacao. */}
        <div key={leadId ?? tela} className="surgir">
          {leadId ? (
            <Lead leadId={leadId} onBack={() => setLeadId(null)} />
          ) : tela === "fila" ? (
            <Fila onOpenLead={setLeadId} />
          ) : tela === "agenda" ? (
            <Agenda />
          ) : tela === "aovivo" ? (
            <AoVivo onOpenLead={setLeadId} />
          ) : tela === "funil" ? (
            <Funil onOpenLead={setLeadId} />
          ) : tela === "analise" ? (
            <Analise />
          ) : tela === "saude" ? (
            <Saude />
          ) : (
            <Config />
          )}
        </div>
      </main>
    </div>
  );
}

function Marca() {
  return (
    <span className="flex items-center gap-2.5">
      <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-accent to-[#1f8f88] grid place-items-center text-ground font-bold text-sm shadow-[0_4px_12px_-4px_rgb(63_203_193/0.6)]">
        A
      </span>
      <span className="font-semibold tracking-tight">Axis</span>
    </span>
  );
}

function Contador({ n, tom }: { n: number; tom: "warm" | "danger" }) {
  return (
    <span
      className={`min-w-5 h-5 px-1.5 rounded-full text-[11px] font-semibold grid place-items-center ${
        tom === "danger" ? "bg-danger-dim text-danger" : "bg-warm-dim text-warm"
      }`}
    >
      {n}
    </span>
  );
}

/*
 * Conexao caida significa que o painel esta mostrando dado velho -- o
 * corretor precisa saber disso, nao descobrir depois.
 */
function Conexao({ online, compacto }: { online: boolean; compacto?: boolean }) {
  return (
    <div
      className={`flex items-center gap-2.5 text-xs ${compacto ? "px-2" : "px-3 h-8"} ${
        online ? "text-ink-3" : "text-danger"
      }`}
      title={online ? "Recebendo atualizações em tempo real" : "Sem conexão com o servidor"}
    >
      <span className="relative flex w-2 h-2">
        {online && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-ok opacity-60 animate-ping" />
        )}
        <span
          className={`relative inline-flex w-2 h-2 rounded-full ${online ? "bg-ok" : "bg-danger pulse"}`}
        />
      </span>
      {!compacto && (online ? "Ao vivo" : "Reconectando…")}
    </div>
  );
}
